// src/components/scheduler/ConfirmationScreen.tsx
import React, { useEffect, useState } from 'react';
import { useScheduler } from './SchedulerContext';
import { AppointmentDetails } from './types';
import { FaCheckCircle, FaCalendarAlt, FaVideo, FaClock } from 'react-icons/fa';

interface ConfirmationScreenProps {
    appointmentId: string;
}

export const ConfirmationScreen: React.FC<ConfirmationScreenProps> = ({ appointmentId }) => {
    const { appointmentData, leadData, apiBaseUrl } = useScheduler();
    const [appointmentDetails, setAppointmentDetails] = useState<AppointmentDetails | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAppointmentDetails();
    }, [appointmentId]);

    const fetchAppointmentDetails = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/api/booking/${appointmentId}`);
            if (response.ok) {
                const data: AppointmentDetails = await response.json();
                setAppointmentDetails(data);
            }
        } catch (err) {
            console.error('Error fetching appointment details:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (isoString: string) => {
        const date = new Date(isoString);
        return {
            date: date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }),
            time: date.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short'
            })
        };
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e2ba57]"></div>
            </div>
        );
    }

    const appointmentDateTime = appointmentDetails
        ? formatDateTime(appointmentDetails.appointment_time)
        : appointmentData
            ? { date: appointmentData.date, time: appointmentData.time }
            : { date: '', time: '' };

    return (
        <div className="text-center max-w-2xl mx-auto">
            <div className="mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                    <FaCheckCircle className="w-10 h-10 text-green-500" />
                </div>

                <h2 className="text-3xl font-bold text-[#212A37] mb-2">
                    Booking Confirmed!
                </h2>
                <p className="text-gray-600">
                    We've sent a confirmation email to {leadData?.email || appointmentDetails?.customer_email}
                </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-[#212A37] mb-4">
                    Appointment Details
                </h3>

                <div className="space-y-3 text-left max-w-sm mx-auto">
                    <div className="flex items-start">
                        <FaCalendarAlt className="text-[#e2ba57] mt-1 mr-3 flex-shrink-0" />
                        <div>
                            <p className="text-sm text-gray-500">Date</p>
                            <p className="font-medium text-[#212A37]">{appointmentDateTime.date}</p>
                        </div>
                    </div>

                    <div className="flex items-start">
                        <FaClock className="text-[#e2ba57] mt-1 mr-3 flex-shrink-0" />
                        <div>
                            <p className="text-sm text-gray-500">Time</p>
                            <p className="font-medium text-[#212A37]">{appointmentDateTime.time}</p>
                        </div>
                    </div>

                    <div className="flex items-start">
                        <FaVideo className="text-[#e2ba57] mt-1 mr-3 flex-shrink-0" />
                        <div>
                            <p className="text-sm text-gray-500">Meeting Type</p>
                            <p className="font-medium text-[#212A37]">Zoom Video Call (30 minutes)</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                <p className="text-sm text-blue-800">
                    <strong>What's Next?</strong> Check your email for the Zoom meeting link and calendar invite.
                    We'll discuss your business goals and how we can help you grow.
                </p>
            </div>

            <div className="space-y-4">
                <h4 className="font-semibold text-[#212A37]">Need to make changes?</h4>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                        href={`/grow-business/reschedule/${appointmentId}`}
                        className="inline-block px-6 py-2 border border-[#e2ba57] text-[#e2ba57] rounded-md hover:bg-[#e2ba57] hover:text-[#212A37] transition-colors"
                    >
                        Reschedule Appointment
                    </a>
                    <a
                        href={`/grow-business/cancel/${appointmentId}`}
                        className="inline-block px-6 py-2 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
                    >
                        Cancel Appointment
                    </a>
                </div>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                    Questions? Email us at{' '}
                    <a href="mailto:ralph.brooks@swiftleaddigital.com" className="text-[#e2ba57] hover:underline">
                        info@swiftleaddigital.com
                    </a>
                </p>
            </div>
        </div>
    );
};